/*=========================================================================================
  File Name: store.js
  Description: Vuex store
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from "vue";
import Vuex from "vuex";
import state from "./state";
import getters from "./getters";
import mutations from "./mutations";
import actions from "./actions";

Vue.use(Vuex);

import moduleAuth from "./auth/moduleAuth.js";
import moduleUserManagement from "./user-management/moduleUserManagement.js";
import moduleRole from "./role/moduleRole.js";
import moduleStudent from "./app/student";
import moduleContact from "./app/contact";
import moduleOperation from "./app/operation";
import moduleFolderEmail from "./app/folder-email";
import moduleTemplateEmail from "./app/template-email";
import moduleMeeting from "./app/meeting";
import moduleDeal from "./app/deal";
import dealOptions from "./app/dealOptions";
import moduleWorkflow from "./app/workflow";
import moduleTicket from "./app/ticket";
import moduleNote from "./app/note";
import moduleLesson from "./lesson/moduleLesson";
import moduleLevel from "./app/level";
import moduleBranch from "./app/branch";
import moduleRoom from "./app/room";
import moduleShift from "./app/shift";
import moduleCourse from "./app/course";
import moduleHoliday from "./app/holiday";
import moduleFreezing from "./app/freezing";
import moduleTopic from "./app/topic";
import moduleTeacher from "./teacher/moduleTeacher";
import province from "./app/province";
import nationality from "./app/nationality";
import certificate from "./app/certificate";
import moduleTestForm from "./app/test-form";
import enrolledCourses from "./student/enrolledCourses";
import studentSchedules from "./student/schedules";
import studentFreezing from "./student/freezing";
import studentPlan from "./student/plan";
import teacherSchedules from "./teacher/schedules";
import teacherReport from "./teacher/report";
import teacherBonus from "./teacher/bonus";
import courseClass from "./course/class";
import studentBonus from "./course/bonus";
import courseStudentFreezing from "./course/freezing";
import courseTimetable from "./course/timetable";
import courseClassStudent from "./course/class/student";
import courseClassTimetable from "./course/class/timetable";
import courseStudentAutoSchedules from "./course/auto-schedules";
import searchHistory from "./search-history";
import studentInClass from "./student-in-class";
import studentHistory from "./student-history";
import teacherReportDetail from "./teacher/report";
import teacherDayOff from "./teacher/day-off";
import teacherWorkingTime from "./teacher/working-time";
import teacherAvailableTime from "./teacher/available-time";
import studentDeal from "./student/deals";
import operations from "./operations";
import stringeeCall from "./app/call";
import appNotification from "./app/notification";
import curriculum from "./app/curriculum";
import combo from "./app/combo";
import voucher from "./app/voucher";
import moduleLibrary from "./lms_admin/library.js";
import moduleTaxonomies from "./lms_admin/taxonomies.js";
import moduleGrammarBlogs from "./lms_admin/grammar-blogs.js";
import moduleLevelLms from "./lms_admin/level-lms.js";
import moduleLevelErp from "./lms_admin/level-erp.js";
import curriculumCourse from "./lms_admin/course.js";
import moduleUpload from "./lms_admin/upload.js";
import customizeLesson from "./app/customize_lesson.js";
import customizeChapter from "./app/customize_chapter.js";
import voucherCourse from "./app/voucher_course.js";

import curriculumLesson from "./lms_admin/lesson.js";
import curriculumSection from "./lms_admin/section.js";
import curriculumBlock from "./lms_admin/block.js";
import libraryDictionary from "./lms_admin/library-dictionary.js";
import moduleOnBoarding from "./lms_admin/onboarding-question.js";
import moduleBadge from "./lms_admin/badge.js";
import moduleMedia from "./lms_admin/media.js";
import moduleQA from "./question-and-answer/moduleQA.js";
import modulePackage from "./package/module";
import modulePermission from "./permission/module";
import moduleLandingPage from "./landing-page/LandingPage.js";
import modulePage from "./page/Page.js";
import modulePillar from "./pillars/Pillars.js";
import moduleBlog from "./blog/Blog.js";
import moduleTeam from "./team/Team.js";
import moduleForm from "./form/Form.js";
import moduleSubmission from "./submission/Submission";
import comboStudent from "./app/comboStudent";
import selfStudyCourse from "./app/self-study-course";
import openai from "./app/openai";

import moduleEducationalConsultant from "./lms_admin/educational-consultant.js";
import feedback from "./app/feedback.js";
import installment from "./app/installment.js";
import report from "./app/report.js";
import stringee from "./app/stringee.js";
import tuition from "./app/tuition.js";

export default new Vuex.Store({
  getters,
  mutations,
  state,
  actions,
  modules: {
    tuition,
    auth: moduleAuth,
    user: moduleUserManagement,
    role: moduleRole,
    student: moduleStudent,
    contact: moduleContact,
    operation: moduleOperation,
    note: moduleNote,
    lesson: moduleLesson,
    level: moduleLevel,
    branch: moduleBranch,
    room: moduleRoom,
    shift: moduleShift,
    course: moduleCourse,
    topic: moduleTopic,
    holiday: moduleHoliday,
    freezing: moduleFreezing,
    testForm: moduleTestForm,
    teacher: moduleTeacher,
    folderEmail: moduleFolderEmail,
    templateEmail: moduleTemplateEmail,
    meeting: moduleMeeting,
    deal: moduleDeal,
    dealOptions,
    workflow: moduleWorkflow,
    ticket: moduleTicket,
    province,
    nationality,
    certificate,
    enrolledCourses,
    studentSchedules,
    courseClassStudent,
    courseClassTimetable,
    courseClass,
    courseTimetable,
    teacherSchedules,
    teacherReport,
    teacherBonus,
    studentFreezing,
    studentPlan,
    searchHistory,
    studentInClass,
    studentHistory,
    teacherReportDetail,
    studentDeal,
    studentBonus,
    courseStudentFreezing,
    courseStudentAutoSchedules,
    teacherDayOff,
    teacherWorkingTime,
    teacherAvailableTime,
    operations,
    stringeeCall,
    appNotification,
    curriculum,

    library: moduleLibrary,
    taxonomies: moduleTaxonomies,
    grammarBlogs: moduleGrammarBlogs,
    levelLms: moduleLevelLms,
    levelErp: moduleLevelErp,
    curriculumCourse,
    curriculumLesson,
    curriculumBlock,
    libraryDictionary,
    curriculumSection,
    upload: moduleUpload,
    onBoarding: moduleOnBoarding,
    badge: moduleBadge,

    qa: moduleQA,

    media: moduleMedia,

    package: modulePackage,

    permission: modulePermission,

    landingPages: moduleLandingPage,
    pages: modulePage,
    pillars: modulePillar,
    blog: moduleBlog,
    team: moduleTeam,
    form: moduleForm,
    submission: moduleSubmission,

    educationalConsultant: moduleEducationalConsultant,
    combo,
    comboStudent,
    selfStudyCourse,
    voucher,

    customizeLesson,
    customizeChapter,
    voucherCourse,
    openai,
    feedback,
    installment,
    report,
    stringee,
  },
  strict: process.env.NODE_ENV !== "production",
});
